import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import api from '../services/api';

export interface PreferenceValue {
  value: string;
  type: string;
  isSynced: boolean;
  updatedAt: string;
}

export interface UserPreferences {
  [key: string]: PreferenceValue;
}

export interface UserPreferencesContextValue {
  preferences: UserPreferences;
  loading: boolean;
  /** True after the first preferences fetch has finished (success or failure). */
  hydrated: boolean;
  error: string | null;
  getPreference: (key: string, defaultValue?: unknown) => unknown;
  setPreference: (key: string, value: unknown, type?: string) => Promise<void>;
  deletePreference: (key: string) => Promise<void>;
  refreshPreferences: () => void;
}

const UserPreferencesContext = createContext<UserPreferencesContextValue | null>(null);

export function UserPreferencesProvider({ children }: { children: ReactNode }) {
  const [preferences, setPreferences] = useState<UserPreferences>({});
  const [loading, setLoading] = useState(true);
  const [hydrated, setHydrated] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const loadGenerationRef = useRef(0);
  const preferencesRef = useRef<UserPreferences>({});
  preferencesRef.current = preferences;

  const loadPreferences = useCallback(async () => {
    const generation = ++loadGenerationRef.current;
    setLoading(true);
    setError(null);

    try {
      const response = await api.getUserPreferences();
      if (generation !== loadGenerationRef.current) return;

      if (response.success) {
        setPreferences(response.data ?? {});
      } else {
        setError(response.message || 'Failed to load preferences');
      }
    } catch (err: unknown) {
      if (generation !== loadGenerationRef.current) return;
      const message =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { message?: string } } }).response?.data?.message
          : undefined;
      console.error('Error loading preferences:', err);
      setError(message || 'Failed to load preferences');
    } finally {
      if (generation === loadGenerationRef.current) {
        setLoading(false);
        setHydrated(true);
      }
    }
  }, []);

  useEffect(() => {
    loadPreferences();
  }, [loadPreferences]);

  const getPreference = useCallback(
    (key: string, defaultValue?: unknown) => {
      const preference = preferences[key];
      if (!preference) return defaultValue;

      const t = String(preference.type ?? 'string').toLowerCase().trim();

      switch (t) {
        case 'boolean':
          if (typeof preference.value === 'boolean') {
            return preference.value;
          }
          {
            const s = String(preference.value).toLowerCase().trim();
            if (s === 'true' || s === '1' || s === 'yes' || s === 'on') return true;
            if (s === 'false' || s === '0' || s === 'no' || s === 'off' || s === '') return false;
            return defaultValue;
          }
        case 'number':
          return parseFloat(String(preference.value));
        case 'json':
          try {
            return JSON.parse(String(preference.value));
          } catch {
            return defaultValue;
          }
        default:
          return preference.value;
      }
    },
    [preferences]
  );

  const setPreference = useCallback(
    async (key: string, value: unknown, type: string = 'string') => {
      try {
        setError(null);

        const current = preferencesRef.current[key];
        if (
          current &&
          String(current.value) === String(value) &&
          String(current.type ?? 'string').toLowerCase() === String(type).toLowerCase()
        ) {
          return;
        }

        setPreferences((prev) => ({
          ...prev,
          [key]: {
            value: String(value),
            type,
            isSynced: false,
            updatedAt: new Date().toISOString(),
          },
        }));

        const response = await api.updateUserPreference(key, value, type);
        if (response.success) {
          setPreferences((prev) => ({
            ...prev,
            [key]: {
              value: response.data.value,
              type: response.data.type,
              isSynced: response.data.isSynced,
              updatedAt: response.data.updatedAt,
            },
          }));
        } else {
          setError(response.message || 'Failed to update preference');
          // Reload to undo optimistic write
          void loadPreferences();
        }
      } catch (err: unknown) {
        console.error('Error setting preference:', err);
        const message =
          err && typeof err === 'object' && 'response' in err
            ? (err as { response?: { data?: { message?: string } } }).response?.data?.message
            : undefined;
        setError(message || 'Failed to update preference');
        void loadPreferences();
      }
    },
    [loadPreferences]
  );

  const deletePreference = useCallback(
    async (key: string) => {
      try {
        setError(null);

        setPreferences((prev) => {
          const next = { ...prev };
          delete next[key];
          return next;
        });

        const response = await api.deleteUserPreference(key);
        if (!response.success) {
          setError(response.message || 'Failed to delete preference');
          void loadPreferences();
        }
      } catch (err: unknown) {
        console.error('Error deleting preference:', err);
        const message =
          err && typeof err === 'object' && 'response' in err
            ? (err as { response?: { data?: { message?: string } } }).response?.data?.message
            : undefined;
        setError(message || 'Failed to delete preference');
        void loadPreferences();
      }
    },
    [loadPreferences]
  );

  const refreshPreferences = useCallback(() => {
    loadPreferences();
  }, [loadPreferences]);

  const value = useMemo<UserPreferencesContextValue>(
    () => ({
      preferences,
      loading,
      hydrated,
      error,
      getPreference,
      setPreference,
      deletePreference,
      refreshPreferences,
    }),
    [
      preferences,
      loading,
      hydrated,
      error,
      getPreference,
      setPreference,
      deletePreference,
      refreshPreferences,
    ]
  );

  return (
    <UserPreferencesContext.Provider value={value}>{children}</UserPreferencesContext.Provider>
  );
}

export function useUserPreferencesContext(): UserPreferencesContextValue {
  const ctx = useContext(UserPreferencesContext);
  if (!ctx) {
    throw new Error('useUserPreferences must be used within UserPreferencesProvider');
  }
  return ctx;
}
